import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sessionActions, orgActions, sultanActions } from '@/store';
import api from '@/lib/api';
import { auth, setAuthToken } from '@/lib/trackizy';
import useBranding from '@/hooks/useBranding';
import { LoginChrome } from './LoginLayouts';

const REMEMBER_KEY = 'trackizy_remembered_email';

export default function LoginPage() {
  const { t } = useTranslation();
  const branding = useBranding();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { user, token } = await auth.login(email, password);
      setAuthToken(token, { remember: rememberMe });
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }

      // Fetch full session (org context, sultan status, server config)
      const [sessionData, server] = await Promise.all([
        auth.session(),
        api.getServer().catch(() => ({})),
      ]);
      // Derive `administrator` bit for Traccar-era call sites — see App.jsx
      // comment. Sultans + org admins alike get the flag.
      const orgRole = sessionData.org?.role;
      const isAdmin = Boolean(sessionData.is_sultan) || orgRole === 'admin';
      dispatch(sessionActions.updateUser({ ...sessionData.user, administrator: isAdmin }));
      dispatch(sessionActions.updateServer(server));
      dispatch(sessionActions.updateExpiry(sessionData.expiry || null));
      dispatch(sessionActions.updateModules(sessionData.modules || []));
      dispatch(sessionActions.updatePlatformBasemaps(sessionData.platformBasemaps || []));
      dispatch(sessionActions.updateIsHub(sessionData.isHub));
      dispatch(sessionActions.updateReseller(sessionData.reseller || null));
      dispatch(sessionActions.updateInstance(sessionData.instance || null));
      if (sessionData.org) {
        const { role, ...orgData } = sessionData.org;
        dispatch(orgActions.setOrg({ org: orgData, role, token: null }));
      }
      if (sessionData.is_sultan) {
        dispatch(sultanActions.setSultan(true));
      }
      // Hub deployment: a sultan's home is the fleet registry, not the org
      // dashboard — the hub has no orgs/devices, so '/' is an empty shell there.
      // Reseller admins with no active org land on their reseller overview
      // instead of the fleet dashboard (they have no fleet to see yet).
      if (sessionData.isHub && sessionData.is_sultan) {
        navigate('/sultan/servers', { replace: true });
      } else if (sessionData.reseller && !sessionData.org) {
        navigate('/reseller', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const form = {
    t,
    color: branding.color,
    email, setEmail,
    password, setPassword,
    rememberMe, setRememberMe,
    error, loading,
    onSubmit: handleSubmit,
  };

  return (
    <LoginChrome design={branding.loginDesign} branding={branding} t={t} form={form} />
  );
}
